import React from "react";

import { isLikelyNumericTimestampLabel } from "./detection";
import { FormattedTimestampText } from "./FormattedTimestampText";

/**
 * Whether the renderer owns a markdown link's label rather than the author.
 * A missing label, or one that reads as a numeric timestamp rendering
 * (`1771481251.468476035`, `t=…ns`, `+1771481426.209115923`), is the AI's
 * attempt at printing the instant and gets replaced with the user's chosen
 * format. Word labels like `start of segment` pass through untouched.
 */
function shouldReplaceLabel(label: React.ReactNode | undefined): boolean {
  if (label === undefined || label === null) {
    return true;
  }
  if (typeof label !== "string") {
    return false;
  }
  return isLikelyNumericTimestampLabel(label);
}

/**
 * Label for a timestamp link rendered inside an `<a>`. Replacement text is a
 * {@link FormattedTimestampText} node, so it re-renders when the user changes
 * format, precision, or t=0 anchor.
 */
export function pickDisplayLabelNode(
  label: React.ReactNode,
  timestampNs: bigint,
): React.ReactNode {
  return shouldReplaceLabel(label) ? (
    <FormattedTimestampText ns={timestampNs} />
  ) : (
    label
  );
}

/**
 * Label for the domain link renderers, whose APIs accept `text: string` and so
 * cannot host a component — the caller passes in its own resolved formatter
 * (see `useFormatTimestamp`). A link carrying no instant keeps its label.
 */
export function pickDisplayLabelString(
  label: string,
  timestampNs: bigint | undefined,
  formatTimestampNs: (ns: bigint) => string,
): string {
  if (timestampNs === undefined) {
    return label;
  }
  if (!shouldReplaceLabel(label)) {
    return label;
  }
  return formatTimestampNs(timestampNs);
}
